import { computed, effect, inject, Injectable, signal } from '@angular/core';

import { AuthService } from '../auth/auth.service';
import {
  AccessCode,
  AppUser,
  AppUserDraft,
  BulkUploadDraft,
  ChannelMetric,
  CodeStatus,
  ControllerVendor,
  ControllerVendorDraft,
  DashboardSnapshot,
  DeliveryChannel,
  GroupAccessCode,
  GroupCodeDraft,
  EventNetwork,
  HospitalityEvent,
  HospitalityEventDraft,
  Location,
  LocationDraft,
  ManualCodeDraft,
  PmsVendor,
  PmsVendorDraft,
  Space,
  SpaceDraft,
  Tenant,
  TenantDraft,
  userLocationIds,
  VendorBinding,
  VendorConfiguration
} from '../models/hospitality';
import {
  ActiveCodesQuery,
  ArchivedCodesQuery,
  BulkJobsQuery,
  ControllerVendorsQuery,
  EventsQuery,
  LocationsQuery,
  PagedControllerVendorResult,
  PagedEventResult,
  PagedAccessCodeResult,
  PagedBulkJobResult,
  PagedLocationResult,
  PagedPmsVendorResult,
  PagedSpaceResult,
  PagedTenantResult,
  PagedUserResult,
  PmsVendorsQuery,
  SpaceListQuery,
  TenantsQuery,
  UsersQuery
} from '../models/hospitality-tables';
import { ApiResponse, unwrapApiResponseData } from '../models/api-response';
import {
  createBulkJobResponseSchema,
  activeUnitCodeResponseSchema,
  activeUnitNetworkQrResponseSchema,
  bulkCreateLocationsResponseSchema,
  bulkCreateUnitsResponseSchema,
  controllerVendorOptionsResponseSchema,
  controllerVendorResponseSchema,
  CreateLocationRequest,
  BulkCreateLocationsRequest,
  BulkCreateUnitsRequest,
  CreateUserRequest,
  CreateUnitRequest,
  deleteControllerVendorResponseSchema,
  pagedControllerVendorsResponseSchema,
  dashboardSnapshotResponseSchema,
  deleteLocationResponseSchema,
  deletePmsVendorResponseSchema,
  deleteSpaceResponseSchema,
  deleteTenantResponseSchema,
  deleteUserResponseSchema,
  extendCodeResponseSchema,
  locationResponseSchema,
  manualCodeResponseSchema,
  groupCodeResponseSchema,
  eventResponseSchema,
  deliveryReceiptResponseSchema,
  pagedActiveCodesResponseSchema,
  pagedArchivedCodesResponseSchema,
  pagedEventNetworksResponseSchema,
  pagedEventsResponseSchema,
  pagedBulkJobsResponseSchema,
  pagedPmsVendorsResponseSchema,
  pmsVendorOptionsResponseSchema,
  pagedLocationsResponseSchema,
  pagedSpacesResponseSchema,
  pagedTenantsResponseSchema,
  pagedUsersResponseSchema,
  pmsVendorResponseSchema,
  revokeCodeResponseSchema,
  spaceResponseSchema,
  tenantResponseSchema,
  UpdateLocationRequest,
  UpdateUnitRequest,
  UpdateUserRequest,
  userResponseSchema
} from '../models/dashboard-api';
import { HospitalityApiService } from './hospitality-api.service';

const CHANNEL_LABELS: Record<DeliveryChannel, string> = {
  tv: 'TV',
  email: 'Email',
  dashboard: 'Dashboard',
  print: 'Print'
};

type SelectionSyncOptions = {
  syncSelection?: boolean;
};

@Injectable({ providedIn: 'root' })
export class HospitalityStoreService {
  private readonly api = inject(HospitalityApiService);
  private readonly auth = inject(AuthService);
  private readonly tenantStorageKey = 'lumen-hospitality-selected-tenant';
  private readonly locationStorageKey = 'lumen-hospitality-selected-location';

  private readonly snapshot = signal<DashboardSnapshot | null>(null);
  private readonly tenantDirectory = signal<Tenant[]>([]);
  readonly controllerVendorOptions = signal<ControllerVendor[]>([]);
  readonly pmsVendorOptions = signal<PmsVendor[]>([]);

  readonly loading = signal(true);
  readonly selectedTenantId = signal('');
  readonly selectedLocationId = signal('');

  readonly tenants = computed(() => this.tenantDirectory().length ? this.tenantDirectory() : this.snapshot()?.tenants ?? []);
  readonly locations = computed(() => this.snapshot()?.locations ?? []);
  readonly spaces = computed(() => this.snapshot()?.spaces ?? []);
  readonly codes = computed(() => this.snapshot()?.codes ?? []);
  readonly events = computed(() => this.snapshot()?.events ?? []);
  readonly bulkJobs = computed(() => this.snapshot()?.bulkJobs ?? []);
  readonly users = computed(() => this.snapshot()?.users ?? []);
  readonly alerts = computed(() => this.snapshot()?.alerts ?? []);

  readonly selectedTenant = computed(
    () => this.tenants().find((tenant) => tenant.id === this.selectedTenantId()) ?? null
  );

  readonly availableLocations = computed(() =>
    this.locations().filter((location) => location.tenantId === this.selectedTenantId())
  );

  readonly selectedLocation = computed(
    () =>
      this.availableLocations().find((location) => location.id === this.selectedLocationId()) ?? null
  );

  readonly locationSpaces = computed(() =>
    this.spaces().filter((space) => space.locationId === this.selectedLocationId())
  );

  readonly locationCodes = computed(() =>
    this.codes()
      .filter((code) => code.locationId === this.selectedLocationId())
      .sort((left, right) => right.validFrom.localeCompare(left.validFrom))
  );
  readonly locationEvents = computed(() =>
    this.events()
      .filter((event) => event.locationId === this.selectedLocationId())
      .sort((left, right) => right.validFrom.localeCompare(left.validFrom))
  );

  readonly activeLocationCodes = computed(() =>
    this.locationCodes().filter((code) => code.status === 'active' || code.status === 'scheduled')
  );

  readonly archivedLocationCodes = computed(() =>
    this.locationCodes().filter((code) => code.status === 'expired' || code.status === 'revoked')
  );

  readonly selectedLocationBulkJobs = computed(() =>
    this.bulkJobs()
      .filter((job) => job.locationId === this.selectedLocationId())
      .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
  );

  readonly selectedLocationAlerts = computed(() =>
    this.alerts().filter((alert) => alert.locationId === this.selectedLocationId())
  );

  readonly selectedLocationUsers = computed(() =>
    this.users().filter((user) => userLocationIds(user.scopes).includes(this.selectedLocationId()))
  );

  readonly selectedLocationChannelMetrics = computed(() =>
    this.buildChannelMetrics(this.locationCodes())
  );

  private loaded = false;
  private tenantLoadVersion = 0;
  private loadedTenantId = '';
  private loadedLocationId = '';

  constructor() {
    effect(() => {
      const tenants = this.tenants();
      const selectedTenantId = this.selectedTenantId();

      if (!tenants.some((tenant) => tenant.id === selectedTenantId)) {
        this.selectedTenantId.set(tenants[0]?.id ?? '');
      }
    });

    effect(() => {
      const locations = this.availableLocations();
      const selectedLocationId = this.selectedLocationId();

      if (!locations.some((location) => location.id === selectedLocationId)) {
        this.selectedLocationId.set(locations[0]?.id ?? '');
      }
    });

    effect(() => {
      if (this.loading()) {
        return;
      }

      localStorage.setItem(this.tenantStorageKey, this.selectedTenantId());
    });

    effect(() => {
      if (this.loading()) {
        return;
      }

      localStorage.setItem(this.locationStorageKey, this.selectedLocationId());
    });
  }

  async load(): Promise<void> {
    if (this.loaded) {
      return;
    }

    if (this.auth.authMode() === 'keycloak' && !this.auth.authenticated()) {
      this.clearForLoggedOutState();
      return;
    }

    const storedTenantId = localStorage.getItem(this.tenantStorageKey) ?? '';
    const storedLocationId = localStorage.getItem(this.locationStorageKey) ?? '';
    const tenantDirectory = await this.tryLoadTenantDirectory();
    const authTenantIds = this.auth.user()?.tenantIds ?? [];
    const tenantId = this.resolveInitialTenantId(storedTenantId, tenantDirectory, authTenantIds);

    this.tenantDirectory.set(tenantDirectory);

    if (tenantId) {
      await this.loadTenantContext(tenantId, storedLocationId, tenantDirectory);
    } else {
      this.snapshot.set(this.emptySnapshot());
      this.selectedTenantId.set('');
      this.selectedLocationId.set('');
    }

    this.loading.set(false);
    this.loaded = true;
  }

  clearForLoggedOutState(): void {
    this.tenantLoadVersion += 1;
    this.snapshot.set(this.emptySnapshot());
    this.tenantDirectory.set([]);
    this.controllerVendorOptions.set([]);
    this.pmsVendorOptions.set([]);
    this.selectedTenantId.set('');
    this.selectedLocationId.set('');
    this.loadedTenantId = '';
    this.loadedLocationId = '';
    this.loading.set(false);
    this.loaded = false;
  }

  async setTenant(tenantId: string): Promise<void> {
    if (!tenantId || tenantId === this.selectedTenantId()) {
      return;
    }

    this.selectedTenantId.set(tenantId);

    if (!this.loaded) {
      return;
    }

    this.selectedLocationId.set('');
    try {
      await this.loadTenantContext(tenantId);
    } catch (error) {
      if (this.selectedTenantId() === tenantId) {
        this.selectedTenantId.set(this.loadedTenantId);
        this.selectedLocationId.set(this.loadedLocationId);
      }

      throw error;
    }
  }

  setLocation(locationId: string): void {
    this.selectedLocationId.set(locationId);
    if (!this.loading() && this.selectedTenantId() === this.loadedTenantId) {
      this.loadedLocationId = locationId;
    }
  }

  locationsByTenant(tenantId: string): Location[] {
    return this.locations().filter((location) => location.tenantId === tenantId);
  }

  spacesByLocation(locationId: string): Space[] {
    return this.spaces().filter((space) => space.locationId === locationId);
  }

  locationById(locationId: string) {
    return this.locations().find((location) => location.id === locationId) ?? null;
  }

  spaceById(spaceId: string) {
    return this.spaces().find((space) => space.id === spaceId) ?? null;
  }

  resolveLocationSsid(locationId: string): string {
    const locationSsid = this.locationById(locationId)?.ssid?.trim();

    if (locationSsid) {
      return locationSsid;
    }

    return this.spacesByLocation(locationId).find((space) => space.ssid?.trim())?.ssid?.trim() ?? '';
  }

  async createManualCode(draft: ManualCodeDraft): Promise<AccessCode> {
    const createdCode = this.parseApiData(
      manualCodeResponseSchema,
      await this.api.createManualCode(
        this.selectedTenantId(),
        draft.locationId,
        {
          firstName: draft.firstName,
          lastName: draft.lastName,
          email: draft.email,
          spaceId: draft.spaceId,
          category: draft.category,
          validFrom: draft.validFrom,
          validUntil: draft.validUntil,
          devicesAllowed: draft.devicesAllowed,
          deliveryChannels: draft.deliveryChannels,
          bookingReference: draft.bookingReference,
          serviceMode: draft.serviceMode,
          notes: draft.notes
        },
        this.spacesByLocation(draft.locationId)
      )
    );

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      codes: [createdCode, ...snapshot.codes]
    }));

    return createdCode;
  }


  async getActiveSpaceCode(spaceId: string): Promise<AccessCode> {
    const space = this.spaceById(spaceId);

    if (!space) {
      throw new Error('Space not found.');
    }

    return this.parseApiData(
      activeUnitCodeResponseSchema,
      await this.api.getActiveSpaceCode(this.selectedTenantId(), space.locationId, space.id)
    );
  }

  async getActiveSpaceNetworkQr(
    spaceId: string
  ): Promise<{ ssid: string; passphrase: string; qrPayload: string }> {
    const space = this.spaceById(spaceId);

    if (!space) {
      throw new Error('Space not found.');
    }

    return this.parseApiData(
      activeUnitNetworkQrResponseSchema,
      await this.api.getActiveSpaceNetworkQr(this.selectedTenantId(), space.locationId, space.id)
    );
  }

  async createGroupCode(spaceId: string, draft: GroupCodeDraft): Promise<GroupAccessCode> {
    const space = this.spaceById(spaceId);

    if (!space) {
      throw new Error('Space not found.');
    }

    return this.parseApiData(
      groupCodeResponseSchema,
      await this.api.createGroupCode(
        this.selectedTenantId(),
        space.locationId,
        space.id,
        draft
      )
    );
  }

  async bulkCreateLocations(
    tenantId: string,
    request: BulkCreateLocationsRequest
  ): Promise<Location[]> {
    const result = this.parseApiData(
      bulkCreateLocationsResponseSchema,
      await this.api.bulkCreateLocations(tenantId, request)
    );

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      locations: [...result.items, ...snapshot.locations]
    }));

    return result.items;
  }

  async bulkCreateSpaces(
    tenantId: string,
    locationId: string,
    request: BulkCreateUnitsRequest
  ): Promise<Space[]> {
    const result = this.parseApiData(
      bulkCreateUnitsResponseSchema,
      await this.api.bulkCreateUnits(tenantId, locationId, request)
    );

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      spaces: [...result.items, ...snapshot.spaces]
    }));

    return result.items;
  }

  async extendCode(codeId: string, hours = 24): Promise<void> {
    const current = this.codes().find((code) => code.id === codeId);

    if (!current) {
      return;
    }

    const updated = this.parseApiData(
      extendCodeResponseSchema,
      await this.api.extendCode(this.selectedTenantId(), current.locationId, codeId, hours)
    );
    this.replaceCode(updated);
  }

  async revokeCode(codeId: string): Promise<void> {
    const current = this.codes().find((code) => code.id === codeId);

    if (!current) {
      return;
    }

    const updated = this.parseApiData(
      revokeCodeResponseSchema,
      await this.api.revokeCode(this.selectedTenantId(), current.locationId, codeId)
    );
    this.replaceCode(updated);
  }

  async sendCodeEmail(codeId: string): Promise<void> {
    const code = this.codes().find((entry) => entry.id === codeId);
    if (!code) return;
    this.parseApiData(
      deliveryReceiptResponseSchema,
      await this.api.sendCodeEmail(this.selectedTenantId(), code.locationId, code.id)
    );
  }

  async saveEvent(draft: HospitalityEventDraft, eventId = ''): Promise<HospitalityEvent> {
    const locationId = this.selectedLocationId();
    const response = eventId
      ? await this.api.updateEvent(this.selectedTenantId(), locationId, eventId, draft)
      : await this.api.createEvent(this.selectedTenantId(), locationId, draft);
    const event = this.parseApiData(eventResponseSchema, response);
    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      events: eventId
        ? snapshot.events.map((entry) => (entry.id === event.id ? event : entry))
        : [event, ...snapshot.events]
    }));
    return event;
  }

  async disableEvent(eventId: string): Promise<void> {
    const event = this.events().find((entry) => entry.id === eventId);
    if (!event) return;
    const updated = this.parseApiData(
      eventResponseSchema,
      await this.api.disableEvent(this.selectedTenantId(), event.locationId, event.id)
    );
    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      events: snapshot.events.map((entry) => (entry.id === updated.id ? updated : entry))
    }));
  }

  async sendEventEmail(eventId: string): Promise<void> {
    const event = this.events().find((entry) => entry.id === eventId);
    if (!event) return;
    this.parseApiData(
      deliveryReceiptResponseSchema,
      await this.api.sendEventEmail(this.selectedTenantId(), event.locationId, event.id)
    );
  }

  async scheduleBulkUpload(
    draft: BulkUploadDraft
  ): Promise<{ jobId: string; imported: number; queueStatus: string }> {
    const result = this.parseApiData(
      createBulkJobResponseSchema,
      await this.api.createBulkJob(
        draft.locationId,
        {
          csvText: draft.csvText,
          mode: draft.mode,
          defaultSpaceId: draft.defaultSpaceId,
          deliveryChannels: draft.deliveryChannels,
          requestedBy: draft.requestedBy,
          ssid: draft.ssid,
          serviceMode: draft.serviceMode
        },
        this.spacesByLocation(draft.locationId)
      )
    );

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      bulkJobs: [result.job, ...snapshot.bulkJobs],
      codes: [...result.codes, ...snapshot.codes]
    }));

    return {
      jobId: result.job.id,
      imported: result.codes.length,
      queueStatus: result.job.queueStatus
    };
  }

  async upsertTenant(draft: TenantDraft, options: SelectionSyncOptions = {}): Promise<Tenant> {
    const existing = draft.id ? this.tenants().find((tenant) => tenant.id === draft.id) : undefined;
    const tenant = draft.id
      ? this.parseApiData(tenantResponseSchema, await this.api.updateTenant(draft.id, draft))
      : this.parseApiData(tenantResponseSchema, await this.api.createTenant(draft));

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      tenants: existing
        ? snapshot.tenants.map((entry) => (entry.id === tenant.id ? tenant : entry))
        : [tenant, ...snapshot.tenants]
    }));
    this.tenantDirectory.update((directory) =>
      directory.some((entry) => entry.id === tenant.id)
        ? directory.map((entry) => (entry.id === tenant.id ? tenant : entry))
        : [tenant, ...directory]
    );

    if (options.syncSelection ?? true) {
      await this.loadTenantContext(tenant.id, '', this.tenantDirectory());
    }

    return tenant;
  }

  async upsertLocation(draft: LocationDraft, options: SelectionSyncOptions = {}): Promise<Location> {
    const locationBody = {
      name: draft.name.trim(),
      city: draft.city.trim(),
      category: draft.category,
      tvIntegration: draft.tvIntegration,
      emailIntegration: draft.emailIntegration,
      state: draft.state,
      note: draft.note.trim() || '-',
      timezone: draft.timezone.trim()
    } satisfies Omit<CreateLocationRequest | UpdateLocationRequest, 'pms' | 'controller'>;
    const normalizedPms = this.normalizeVendorBinding(draft.pms);
    const normalizedController = this.normalizeVendorBinding(draft.controller);
    const locationRequest = {
      ...locationBody,
      ...(normalizedPms ? { pms: normalizedPms } : {}),
      ...(normalizedController ? { controller: normalizedController } : {})
    } satisfies CreateLocationRequest | UpdateLocationRequest;
    const location = draft.id
      ? this.parseApiData(
          locationResponseSchema,
          await this.api.updateLocation(draft.tenantId, draft.id, locationRequest)
        )
      : this.parseApiData(
          locationResponseSchema,
          await this.api.createLocation(draft.tenantId, locationRequest)
        );
    const existing = draft.id
      ? this.locations().find((entry) => entry.id === location.id)
      : undefined;

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      locations: existing
        ? snapshot.locations.map((entry) => (entry.id === location.id ? location : entry))
        : [location, ...snapshot.locations]
    }));

    if (options.syncSelection ?? true) {
      this.selectedTenantId.set(location.tenantId);
      this.selectedLocationId.set(location.id);
    }

    return location;
  }

  async upsertUser(draft: AppUserDraft): Promise<AppUser> {
    const userBody = this.buildUserRequest(draft);
    const user = draft.id
      ? this.parseApiData(userResponseSchema, await this.api.updateUser(draft.id, userBody))
      : this.parseApiData(userResponseSchema, await this.api.createUser(userBody));
    const existing = draft.id ? this.users().find((entry) => entry.id === user.id) : undefined;

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      users: existing
        ? snapshot.users.map((entry) => (entry.id === user.id ? user : entry))
        : [user, ...snapshot.users]
    }));

    return user;
  }

  async deleteTenant(tenantId: string): Promise<void> {
    this.parseApiData(deleteTenantResponseSchema, await this.api.deleteTenant(tenantId));
    const locationIds = this.locations()
      .filter((location) => location.tenantId === tenantId)
      .map((location) => location.id);
    const spaceIds = this.spaces()
      .filter((space) => locationIds.includes(space.locationId))
      .map((space) => space.id);

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      tenants: snapshot.tenants.filter((tenant) => tenant.id !== tenantId),
      locations: snapshot.locations.filter((location) => location.tenantId !== tenantId),
      spaces: snapshot.spaces.filter((space) => !locationIds.includes(space.locationId)),
      codes: snapshot.codes.filter(
        (code) => !locationIds.includes(code.locationId) && !spaceIds.includes(code.spaceId)
      ),
      events: snapshot.events.filter((event) => !locationIds.includes(event.locationId)),
      bulkJobs: snapshot.bulkJobs.filter((job) => !locationIds.includes(job.locationId)),
      alerts: snapshot.alerts.filter((alert) => !locationIds.includes(alert.locationId)),
      users: snapshot.users.map((user) => ({
        ...user,
        scopes: user.scopes
          .filter((scope) => scope.tenantId !== tenantId)
          .map((scope) => ({
            ...scope,
            locationIds: scope.locationIds.filter((id) => !locationIds.includes(id))
          }))
          }))
    }));
    this.tenantDirectory.update((directory) => directory.filter((tenant) => tenant.id !== tenantId));

    if (this.selectedTenantId() === tenantId) {
      const nextTenantId = this.tenantDirectory()[0]?.id ?? '';

      if (nextTenantId) {
        await this.loadTenantContext(nextTenantId, '', this.tenantDirectory());
        return;
      }

      this.snapshot.set(this.emptySnapshot());
      this.selectedTenantId.set('');
      this.selectedLocationId.set('');
      this.loadedTenantId = '';
      this.loadedLocationId = '';
    }
  }

  async deleteLocation(locationId: string): Promise<void> {
    const tenantId = this.locationById(locationId)?.tenantId ?? '';
    this.parseApiData(
      deleteLocationResponseSchema,
      await this.api.deleteLocation(tenantId, locationId)
    );
    const spaceIds = this.spacesByLocation(locationId).map((space) => space.id);

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      locations: snapshot.locations.filter((entry) => entry.id !== locationId),
      spaces: snapshot.spaces.filter((space) => space.locationId !== locationId),
      codes: snapshot.codes.filter(
        (code) => code.locationId !== locationId && !spaceIds.includes(code.spaceId)
      ),
      events: snapshot.events.filter((event) => event.locationId !== locationId),
      bulkJobs: snapshot.bulkJobs.filter((job) => job.locationId !== locationId),
      alerts: snapshot.alerts.filter((alert) => alert.locationId !== locationId),
      users: snapshot.users.map((user) => ({
        ...user,
        scopes: user.scopes.map((scope) => ({
          ...scope,
          locationIds: scope.locationIds.filter((id) => id !== locationId)
        }))
      }))
    }));
  }

  async deleteUser(userId: string): Promise<void> {
    this.parseApiData(deleteUserResponseSchema, await this.api.deleteUser(userId));
    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      users: snapshot.users.filter((user) => user.id !== userId)
    }));
  }

  async upsertSpace(draft: SpaceDraft, options: SelectionSyncOptions = {}): Promise<Space> {
    const spaceBody = {
      name: draft.name,
      type: draft.type,
      vlanId: draft.vlanId,
      serviceVlanId: draft.serviceVlanId,
      networkId: draft.networkId,
      panMode: draft.panMode,
      capacity: draft.capacity,
      assignment: draft.assignment,
      status: draft.status
    } satisfies CreateUnitRequest | UpdateUnitRequest;
    const tenantId = this.locationById(draft.locationId)?.tenantId ?? this.selectedTenantId();
    const space = draft.id
      ? this.parseApiData(
          spaceResponseSchema,
          await this.api.updateUnit(tenantId, draft.locationId, draft.id, spaceBody)
        )
      : this.parseApiData(
          spaceResponseSchema,
          await this.api.createUnit(tenantId, draft.locationId, spaceBody)
        );
    const existing = draft.id ? this.spaces().find((entry) => entry.id === space.id) : undefined;

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      spaces: existing
        ? snapshot.spaces.map((entry) => (entry.id === space.id ? space : entry))
        : [space, ...snapshot.spaces]
    }));

    if (options.syncSelection ?? true) {
      this.selectedLocationId.set(space.locationId);
    }

    return space;
  }

  async deleteSpace(spaceId: string): Promise<void> {
    const space = this.spaceById(spaceId);

    if (!space) {
      return;
    }

    const tenantId = this.locationById(space.locationId)?.tenantId ?? this.selectedTenantId();
    this.parseApiData(
      deleteSpaceResponseSchema,
      await this.api.deleteUnit(tenantId, space.locationId, spaceId)
    );
    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      spaces: snapshot.spaces.filter((space) => space.id !== spaceId),
      codes: snapshot.codes.filter((code) => code.spaceId !== spaceId)
    }));
  }

  toggleUserStatus(userId: string): void {
    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      users: snapshot.users.map((user) =>
        user.id === userId
          ? {
              ...user,
              status: user.status === 'disabled' ? 'active' : 'disabled'
            }
          : user
      )
    }));
  }

  generateQrCode(payload: string): Promise<string> {
    return this.api.generateQrCode(payload);
  }

  async fetchLocationSpaces(query: SpaceListQuery): Promise<PagedSpaceResult> {
    return this.parseApiData(pagedSpacesResponseSchema, await this.api.listLocationSpaces(query));
  }

  async fetchActiveLocationCodes(query: ActiveCodesQuery): Promise<PagedAccessCodeResult> {
    return this.parseApiData(
      pagedActiveCodesResponseSchema,
      await this.api.listActiveLocationCodes(query)
    );
  }

  async fetchArchivedLocationCodes(query: ArchivedCodesQuery): Promise<PagedAccessCodeResult> {
    return this.parseApiData(
      pagedArchivedCodesResponseSchema,
      await this.api.listArchivedLocationCodes(query)
    );
  }

  async fetchLocationEvents(query: EventsQuery): Promise<PagedEventResult> {
    const result = this.parseApiData(
      pagedEventsResponseSchema,
      await this.api.listLocationEvents(query)
    );

    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      events: [
        ...snapshot.events.filter((event) => event.locationId !== query.locationId),
        ...result.items
      ]
    }));
    return result;
  }

  async fetchAllLocationEventNetworks(tenantId: string, locationId: string): Promise<EventNetwork[]> {
    const pageSize = 200;
    const networks: EventNetwork[] = [];
    for (let page = 1; ; page++) {
      const result = this.parseApiData(
        pagedEventNetworksResponseSchema,
        await this.api.listLocationEventNetworks({ tenantId, locationId, page, pageSize })
      );
      networks.push(...result.items);
      if (result.items.length === 0 || page >= result.pageCount || networks.length >= result.total) {
        return networks;
      }
    }
  }

  async fetchLocationBulkJobs(query: BulkJobsQuery): Promise<PagedBulkJobResult> {
    return this.parseApiData(pagedBulkJobsResponseSchema, await this.api.listLocationBulkJobs(query));
  }

  async fetchTenants(query: TenantsQuery): Promise<PagedTenantResult> {
    return this.parseApiData(pagedTenantsResponseSchema, await this.api.listTenants(query));
  }

  async fetchUsers(query: UsersQuery): Promise<PagedUserResult> {
    return this.parseApiData(pagedUsersResponseSchema, await this.api.listUsers(query));
  }

  async fetchLocations(query: LocationsQuery): Promise<PagedLocationResult> {
    return this.parseApiData(pagedLocationsResponseSchema, await this.api.listLocations(query));
  }

  async fetchControllerVendors(query: ControllerVendorsQuery): Promise<PagedControllerVendorResult> {
    return this.parseApiData(
      pagedControllerVendorsResponseSchema,
      await this.api.listControllerVendors(query)
    );
  }

  async fetchPmsVendors(query: PmsVendorsQuery): Promise<PagedPmsVendorResult> {
    return this.parseApiData(pagedPmsVendorsResponseSchema, await this.api.listPmsVendors(query));
  }

  async fetchControllerVendorOptions(force = false): Promise<ControllerVendor[]> {
    if (this.controllerVendorOptions().length && !force) {
      return this.controllerVendorOptions();
    }

    const vendors = this.parseApiData(
      controllerVendorOptionsResponseSchema,
      await this.api.listControllerVendorOptions()
    );
    this.controllerVendorOptions.set(vendors);
    return vendors;
  }

  async fetchPmsVendorOptions(force = false): Promise<PmsVendor[]> {
    if (this.pmsVendorOptions().length && !force) {
      return this.pmsVendorOptions();
    }

    const vendors = this.parseApiData(
      pmsVendorOptionsResponseSchema,
      await this.api.listPmsVendorOptions()
    );
    this.pmsVendorOptions.set(vendors);
    return vendors;
  }

  async fetchControllerVendor(vendorId: string): Promise<ControllerVendor> {
    const cached = this.controllerVendorOptions().find((vendor) => vendor.id === vendorId);

    if (cached) {
      return cached;
    }

    const vendor = this.parseApiData(
      controllerVendorResponseSchema,
      await this.api.getControllerVendor(vendorId)
    );
    this.controllerVendorOptions.update((vendors) =>
      vendors.some((entry) => entry.id === vendor.id)
        ? vendors.map((entry) => (entry.id === vendor.id ? vendor : entry))
        : [...vendors, vendor]
    );
    return vendor;
  }

  async fetchPmsVendor(vendorId: string): Promise<PmsVendor> {
    const cached = this.pmsVendorOptions().find((vendor) => vendor.id === vendorId);

    if (cached) {
      return cached;
    }

    const vendor = this.parseApiData(pmsVendorResponseSchema, await this.api.getPmsVendor(vendorId));
    this.pmsVendorOptions.update((vendors) =>
      vendors.some((entry) => entry.id === vendor.id)
        ? vendors.map((entry) => (entry.id === vendor.id ? vendor : entry))
        : [...vendors, vendor]
    );
    return vendor;
  }

  async createControllerVendor(draft: ControllerVendorDraft): Promise<ControllerVendor> {
    const vendor = this.parseApiData(
      controllerVendorResponseSchema,
      await this.api.createControllerVendor({
        name: draft.name.trim(),
        fields: this.normalizeVendorFields(draft.fields)
      })
    );
    this.controllerVendorOptions.update((vendors) => [vendor, ...vendors.filter((entry) => entry.id !== vendor.id)]);
    return vendor;
  }

  async createPmsVendor(draft: PmsVendorDraft): Promise<PmsVendor> {
    const vendor = this.parseApiData(
      pmsVendorResponseSchema,
      await this.api.createPmsVendor({
        name: draft.name.trim(),
        fields: this.normalizeVendorFields(draft.fields)
      })
    );
    this.pmsVendorOptions.update((vendors) => [vendor, ...vendors.filter((entry) => entry.id !== vendor.id)]);
    return vendor;
  }

  async updateControllerVendor(vendorId: string, draft: ControllerVendorDraft): Promise<ControllerVendor> {
    const vendor = this.parseApiData(
      controllerVendorResponseSchema,
      await this.api.updateControllerVendor(vendorId, {
        name: draft.name.trim(),
        fields: this.normalizeVendorFields(draft.fields)
      })
    );
    this.controllerVendorOptions.update((vendors) =>
      vendors.some((entry) => entry.id === vendor.id)
        ? vendors.map((entry) => (entry.id === vendor.id ? vendor : entry))
        : [vendor, ...vendors]
    );
    return vendor;
  }

  async updatePmsVendor(vendorId: string, draft: PmsVendorDraft): Promise<PmsVendor> {
    const vendor = this.parseApiData(
      pmsVendorResponseSchema,
      await this.api.updatePmsVendor(vendorId, {
        name: draft.name.trim(),
        fields: this.normalizeVendorFields(draft.fields)
      })
    );
    this.pmsVendorOptions.update((vendors) =>
      vendors.some((entry) => entry.id === vendor.id)
        ? vendors.map((entry) => (entry.id === vendor.id ? vendor : entry))
        : [vendor, ...vendors]
    );
    return vendor;
  }

  async deleteControllerVendor(vendorId: string): Promise<void> {
    this.parseApiData(deleteControllerVendorResponseSchema, await this.api.deleteControllerVendor(vendorId));
    this.controllerVendorOptions.update((vendors) => vendors.filter((vendor) => vendor.id !== vendorId));
  }

  async deletePmsVendor(vendorId: string): Promise<void> {
    this.parseApiData(deletePmsVendorResponseSchema, await this.api.deletePmsVendor(vendorId));
    this.pmsVendorOptions.update((vendors) => vendors.filter((vendor) => vendor.id !== vendorId));
  }

  resolveControllerVendorName(
    binding: Pick<VendorBinding, 'vendorId'> | VendorBinding | null | undefined
  ): string {
    if (!binding?.vendorId) {
      return '';
    }

    return (
      this.controllerVendorOptions().find((vendor) => vendor.id === binding.vendorId)?.name ?? binding.vendorId
    );
  }

  resolvePmsVendorName(
    binding: Pick<VendorBinding, 'vendorId'> | VendorBinding | null | undefined
  ): string {
    if (!binding?.vendorId) {
      return '';
    }

    return this.pmsVendorOptions().find((vendor) => vendor.id === binding.vendorId)?.name ?? binding.vendorId;
  }

  metricsForCodes(codes: AccessCode[]): ChannelMetric[] {
    return this.buildChannelMetrics(codes);
  }

  countStatuses(codes: AccessCode[]): Record<CodeStatus, number> {
    return codes.reduce<Record<CodeStatus, number>>(
      (counts, code) => {
        counts[code.status] += 1;
        return counts;
      },
      {
        active: 0,
        scheduled: 0,
        expired: 0,
        revoked: 0
      }
    );
  }

  private replaceCode(updatedCode: AccessCode): void {
    this.updateSnapshot((snapshot) => ({
      ...snapshot,
      codes: snapshot.codes.map((code) => (code.id === updatedCode.id ? updatedCode : code))
    }));
  }

  private updateSnapshot(mutator: (snapshot: DashboardSnapshot) => DashboardSnapshot): void {
    this.snapshot.update((snapshot) => {
      if (!snapshot) {
        return snapshot;
      }

      const nextSnapshot = this.normalizeSnapshot(mutator(snapshot));
      this.api.replaceSnapshot(nextSnapshot);
      return nextSnapshot;
    });
  }

  private async tryLoadTenantDirectory(): Promise<Tenant[]> {
    try {
      const page = this.parseApiData(
        pagedTenantsResponseSchema,
        await this.api.listTenants({
          page: 1,
          pageSize: 200,
          sortKey: 'name',
          sortDirection: 'asc'
        })
      );

      return page.items;
    } catch {
      return [];
    }
  }

  private resolveInitialTenantId(
    storedTenantId: string,
    tenantDirectory: Tenant[],
    authTenantIds: string[]
  ): string {
    const availableTenantIds = tenantDirectory.length
      ? tenantDirectory.map((tenant) => tenant.id)
      : authTenantIds;

    if (storedTenantId && availableTenantIds.includes(storedTenantId)) {
      return storedTenantId;
    }

    return availableTenantIds[0] ?? storedTenantId;
  }

  private async loadTenantContext(
    tenantId: string,
    preferredLocationId = '',
    tenantDirectory: Tenant[] = this.tenantDirectory()
  ): Promise<void> {
    const requestVersion = ++this.tenantLoadVersion;
    this.loading.set(true);

    try {
      const snapshot = this.normalizeSnapshot(
        this.parseApiData(
          dashboardSnapshotResponseSchema,
          await this.api.loadDashboardSnapshot(tenantId)
        )
      );
      const locationId = snapshot.locations.some((location) => location.id === preferredLocationId)
        ? preferredLocationId
        : snapshot.locations[0]?.id ?? '';

      if (requestVersion !== this.tenantLoadVersion) {
        return;
      }

      this.snapshot.set({
        ...snapshot,
        tenants: tenantDirectory.length ? tenantDirectory : snapshot.tenants
      });
      this.api.replaceSnapshot(snapshot);
      this.loadedTenantId = tenantId;
      this.loadedLocationId = locationId;
      this.selectedTenantId.set(tenantId);
      this.selectedLocationId.set(locationId);
      if (!tenantDirectory.length) {
        this.tenantDirectory.set(snapshot.tenants);
      }
    } finally {
      if (requestVersion === this.tenantLoadVersion) {
        this.loading.set(false);
      }
    }
  }

  private parseApiData<T>(
    schema: {
      parse: (input: unknown) => ApiResponse<T>;
    },
    response: unknown
  ): T {
    return unwrapApiResponseData(schema.parse(response));
  }

  private normalizeSnapshot(snapshot: DashboardSnapshot): DashboardSnapshot {
    const locations = snapshot.locations.map((location) => ({
      ...location,
      ssid:
        location.ssid ||
        snapshot.spaces.find((space) => space.locationId === location.id)?.ssid?.trim() ||
        ''
    }));

    return {
      ...snapshot,
      locations,
      tenants: snapshot.tenants.map((tenant) => ({
        ...tenant,
        properties: locations.some((location) => location.tenantId === tenant.id)
          ? locations.filter((location) => location.tenantId === tenant.id).length
          : tenant.properties,
        openAlerts: locations.some((location) => location.tenantId === tenant.id)
          ? snapshot.alerts.filter((alert) =>
              locations.some(
                (location) => location.id === alert.locationId && location.tenantId === tenant.id
              )
            ).length
          : tenant.openAlerts
      }))
    };
  }

  private emptySnapshot(): DashboardSnapshot {
    return {
      tenants: [],
      locations: [],
      spaces: [],
      codes: [],
      events: [],
      bulkJobs: [],
      users: [],
      alerts: []
    };
  }

  private buildUserRequest(draft: AppUserDraft): CreateUserRequest | UpdateUserRequest {
    const scopes = draft.scopes.map((scope) => ({
      tenantId: scope.tenantId,
      locationIds: [...new Set(scope.locationIds)]
    }));

    return {
      name: draft.name,
      email: draft.email,
      role: draft.role,
      scopes,
      status: draft.status
    };
  }

  private buildChannelMetrics(codes: AccessCode[]): ChannelMetric[] {
    const counts = new Map<DeliveryChannel, number>([
      ['tv', 0],
      ['email', 0],
      ['dashboard', 0],
      ['print', 0]
    ]);

    for (const code of codes) {
      for (const channel of code.deliveryChannels) {
        counts.set(channel, (counts.get(channel) ?? 0) + 1);
      }
    }

    const total = Array.from(counts.values()).reduce((sum, count) => sum + count, 0) || 1;

    return Array.from(counts.entries())
      .map(([channel, count]) => ({
        channel,
        label: CHANNEL_LABELS[channel],
        count,
        share: Math.round((count / total) * 100)
      }))
      .sort((left, right) => right.count - left.count);
  }

  private normalizeVendorFields(
    fields: ControllerVendorDraft['fields'] | PmsVendorDraft['fields']
  ): Record<string, ControllerVendorDraft['fields'][string]> {
    return Object.fromEntries(
      Object.entries(fields)
        .map(([key, value]) => [key.trim(), value] as const)
        .filter(([key]) => !!key)
    );
  }

  private normalizeVendorBinding(binding: VendorBinding | null | undefined): VendorBinding | null {
    if (!binding?.vendorId.trim()) {
      return null;
    }

    return {
      vendorId: binding.vendorId.trim(),
      configuration: this.normalizeVendorConfiguration(binding.configuration)
    };
  }

  private normalizeVendorConfiguration(configuration: VendorConfiguration): VendorConfiguration {
    return Object.fromEntries(
      Object.entries(configuration).map(([key, value]) => [
        key.trim(),
        typeof value === 'string' ? value.trim() : value
      ])
    );
  }
}
